import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ContactFormValue } from '../models/contact/contact-form-group.interface';
import { BehaviorSubject, Observable, catchError, tap, throwError } from 'rxjs';
import { Contact } from '../models/contact/contact.interface';
import { DetailedContact } from '../models/contact/detailed-contact.interface';
import { RequestModel } from '../models/request-model.interface';
import { PagedList } from '../models/paged-list.interface';
import { ContactDeliveryAddress } from '../models/contact/contact-delivery-address.interface';
import { ContactActivity } from '../models/contact/contact-activity.interface';
import { ContactNote } from '../models/contact/contact-note.interface';
import { Attachment } from '../models/project/attachment/attachment.interface';

@Injectable()
export class ContactService {
  private httpClient = inject(HttpClient);

  private readonly organizationContactsCache = new Map<string, BehaviorSubject<Contact[]>>();
  private readonly companyContactsCache = new Map<string, BehaviorSubject<Contact[]>>();

  private contactsUrl = '';

  public setContactsUrl(companyId: string) {
    this.contactsUrl = `${environment.apiUrl}/companies/${companyId}/contacts`;
  }

  public loadCompanyContacts(
    companyId: string,
    excludedOrganizationId?: string,
  ): Observable<Contact[]> {
    let params = new HttpParams();

    if (excludedOrganizationId) {
      params = params.set('excludedOrganizationId', excludedOrganizationId);
    }

    if (!this.companyContactsCache.has(companyId)) {
      this.companyContactsCache.set(companyId, new BehaviorSubject<Contact[]>([]));
    }

    return this.httpClient.get<Contact[]>(this.contactsUrl, { params }).pipe(
      tap(contacts => {
        this.companyContactsCache.get(companyId)!.next(contacts);
      }),
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public getCompanyContacts(companyId: string): Observable<Contact[]> {
    if (!this.companyContactsCache.has(companyId)) {
      this.companyContactsCache.set(companyId, new BehaviorSubject<Contact[]>([]));
    }

    return this.companyContactsCache.get(companyId)!.asObservable();
  }

  public getCompanyContactsPaged(
    request: RequestModel,
    excludedOrganizationId?: string,
  ): Observable<PagedList<Contact>> {
    let params = new HttpParams();

    if (excludedOrganizationId) {
      params = params.set('excludedOrganizationId', excludedOrganizationId);
    }

    return this.httpClient
      .post<PagedList<Contact>>(`${this.contactsUrl}/company-contacts`, request, {
        params,
      })
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public loadOrganizationContacts(
    companyId: string,
    organizationId: string,
  ): Observable<Contact[]> {
    const key = `${companyId}::${organizationId}`;
    const params = new HttpParams().set('organizationId', organizationId);

    if (!this.organizationContactsCache.has(key)) {
      this.organizationContactsCache.set(key, new BehaviorSubject<Contact[]>([]));
    }

    return this.httpClient
      .get<Contact[]>(`${this.contactsUrl}/organization`, { params: params })
      .pipe(
        tap(contacts => {
          this.organizationContactsCache.get(key)!.next(contacts);
        }),
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getOrganizationContacts(companyId: string, organizationId: string): Observable<Contact[]> {
    const key = `${companyId}::${organizationId}`;

    if (!this.organizationContactsCache.has(key)) {
      this.organizationContactsCache.set(key, new BehaviorSubject<Contact[]>([]));
    }

    return this.organizationContactsCache.get(key)!.asObservable();
  }

  public getContact(contactId: string): Observable<DetailedContact> {
    return this.httpClient.get<DetailedContact>(`${this.contactsUrl}/${contactId}`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public getContactActivity(contactId: string): Observable<ContactActivity[]> {
    return this.httpClient.get<ContactActivity[]>(`${this.contactsUrl}/${contactId}/activity`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public getContactNotes(contactId: string): Observable<ContactNote[]> {
    return this.httpClient.get<ContactNote[]>(`${this.contactsUrl}/${contactId}/notes`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public createContactNote(contactId: string, text: string): Observable<ContactNote> {
    return this.httpClient.post<ContactNote>(`${this.contactsUrl}/${contactId}/notes`, { text }).pipe(
      catchError((error: unknown) => {
        return throwError(() => this.mapApiError(error));
      }),
    );
  }

  public getContactFiles(contactId: string): Observable<Attachment[]> {
    return this.httpClient.get<Attachment[]>(`${this.contactsUrl}/${contactId}/attachments`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public createContactFile(
    contactId: string,
    attachment: Attachment,
    correlationId?: string,
  ): Observable<Attachment> {
    return this.httpClient.post<Attachment>(`${this.contactsUrl}/${contactId}/attachments`, attachment, {
      headers: this.buildHeaders(correlationId),
    });
  }

  public deleteContactFile(contactId: string, attachmentId: string): Observable<null> {
    return this.httpClient.delete<null>(`${this.contactsUrl}/${contactId}/attachments/${attachmentId}`);
  }

  public createContact(contact: ContactFormValue): Observable<Contact> {
    return this.httpClient.post<Contact>(this.contactsUrl, contact).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public patchContact(contactId: string, field: string, value: unknown): Observable<null> {
    return this.patchContactFields(contactId, { [field]: value });
  }

  public patchContactFields(contactId: string, patchPayload: Record<string, unknown>): Observable<null> {
    return this.httpClient.patch<null>(`${this.contactsUrl}/${contactId}`, patchPayload, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
  }

  public updateDeliveryAddresses(
    contactId: string,
    deliveryAddresses: ContactDeliveryAddress[],
  ): Observable<ContactDeliveryAddress[]> {
    return this.httpClient.put<ContactDeliveryAddress[]>(
      `${this.contactsUrl}/${contactId}/delivery-addresses`,
      { deliveryAddresses },
    );
  }

  private mapApiError(error: unknown): Error {
    if (error instanceof Error && !(error instanceof HttpErrorResponse)) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.extractServerMessage(error.error);
      return new Error(serverMessage || error.message || 'Unexpected error');
    }

    return new Error('Unexpected error');
  }

  private extractServerMessage(errorBody: unknown): string | null {
    if (typeof errorBody === 'string' && errorBody.trim().length > 0) {
      return errorBody;
    }

    if (typeof errorBody === 'object' && errorBody !== null) {
      const message = (errorBody as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
    }

    return null;
  }

  private buildHeaders(correlationId?: string): HttpHeaders | undefined {
    if (!correlationId) {
      return undefined;
    }

    return new HttpHeaders({
      'X-Correlation-Id': correlationId,
    });
  }

  public deleteContact(contactId: string): Observable<null> {
    return this.httpClient.delete<null>(`${this.contactsUrl}/${contactId}`).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public addToOrganization(companyId: string, organizationId: string, contact: Contact): void {
    this.companyContactsCache.get(companyId)?.next(
      this.companyContactsCache
        .get(companyId)!
        .getValue()
        .filter(c => c.id !== contact.id),
    );

    const orgKey = `${companyId}::${organizationId}`;

    if (!this.organizationContactsCache.has(orgKey)) {
      this.organizationContactsCache.set(orgKey, new BehaviorSubject<Contact[]>([]));
    }

    this.organizationContactsCache
      .get(orgKey)!
      .next([...this.organizationContactsCache.get(orgKey)!.getValue(), contact]);
  }

  public getCompanyContactById(companyId: string, contactId: string): Contact | undefined {
    const subject = this.companyContactsCache.get(companyId);
    if (!subject) {
      return undefined;
    }

    return subject.getValue().find(c => c.id === contactId);
  }

  public hasOrganizationContacts(companyId: string, organizationId: string): boolean {
    const key = `${companyId}::${organizationId}`;
    return this.organizationContactsCache.has(key);
  }

  public hasCompanyContacts(companyId: string): boolean {
    return this.companyContactsCache.has(companyId);
  }
}
